import { TreeviewItem } from '../../lib/ngx-treeview';
import { Territory } from '../../models/territory/territory';
import { TerritoryKind } from '../../core/territory-kind/territory-kind.enum';
import { frenchDepartments } from '../../components/smart-search-modal/french-departments';

export const TerritoryHelper = {
  transferToTreeview,
  transferToList,
  getDisplayedPerimeter,
  collectTerritoryData,
};

function containsTerritoryOutsideDepartment(element: Territory) {
  return (element.name === 'outside_department') || (element.name === 'outside_Metropolis_Greater_Paris') ;
}

function getTerritoryChildren(territory: Territory) {
  let children = territory.territories.filter((child) => !child.isSyndicate);

  if (territory.kind === TerritoryKind.EPCI && territory.territories.length >= 2 && territory.territories[1].name === 'outside_department') {
    const territory_outside_department = new Territory({
      name: 'outside_department',
      uid: '',
      territories: [],
      search_key: '',
      kind: '',
      code: '',
      outside_department_count: territory.territories[1].territories.length
    });

    if (territory.territories[0].territories[0].name === 'Paris') {
      territory_outside_department.name = 'outside_Metropolis_Greater_Paris';
    }
    if (!territory.territories[0].territories.some(containsTerritoryOutsideDepartment)) {
      territory.territories[0].territories.push(territory_outside_department);
    }

    children = territory.territories[0].territories;

  }
  return children;
}
function transferToTreeview(
  territories: Array<Territory>,
  isRoot: boolean,
  collapsed = false,
  checked: boolean | Array<string> = true
): TreeviewItem[] {
  return territories.map((territory) => {
    const children = getTerritoryChildren(territory);
    const uid = territory.uid;
    return new TreeviewItem({
      text: mapDepartementName(territory),
      value: territory.uid,
      isRoot: isRoot,
      collapsed: collapsed && !isRoot,
      outside_department_count: territory.outside_department_count,
      infos: {
        kind: territory.kind,
        subkind: territory.subkind,
        code: territory.code,
        articleCount: territory.articleCount,
        adminDocCount: territory.adminDocCount
      },
      checked: typeof checked === 'boolean' ? checked : checked.includes(uid),
      children: transferToTreeview(children, false, collapsed, checked)
    });
  });
}

function mapDepartementName(territory: Territory) {
  if (territory.kind === TerritoryKind.DEPARTMENT) {
    return territory.name + ` (${territory.region?.name})`;
  } else return territory.name
}

function transferToList(territories: Array<Territory>): Set<string> {
  const territoriesList = new Set<string>();
  territories.forEach((territory) => {
    if (territory.uid) {
      territoriesList.add(territory.uid);
    }
    const children = getTerritoryChildren(territory);
    transferToList(children).forEach(c => territoriesList.add(c));
  });
  return territoriesList;
}
export function territoriesToUIDArray(territories: Array<Territory>) : Array<any>{
  return territories.reduce((prev: Array<string>, territory: Territory) => [...prev, territory.uid, ...territoriesToUIDArray(territory.territories)], []);
}

export function makeSyndicatesTreeview(tree: {[p: string]: Territory[]}, rootText= 'All') {
  const scopeFilter = Object.entries(tree).map(
    ([scope, syndicates]) => new TreeviewItem({
      text: scope,
      value: '',
      checked: true,
      collapsed: true,
      infos: {type: 'syndicates'},
      children: syndicates.map((syndicate) => new TreeviewItem({
        text: syndicate.name,
        value: syndicate.uid,
        checked: true,
        infos: {
          type: 'syndicates',
          fullname: syndicate.syndicateName
        }
      }))
    }));
  return [new TreeviewItem({
    text: rootText,
    value: '',
    isRoot: true,
    checked: true,
    infos: {type: 'syndicates'},
    children: scopeFilter
  })];
}

export function departmentContainsEpt(departmentCode: string) {
  const departments_with_epts = ['95', '94', '93', '92', '91'];
  return departments_with_epts.includes(departmentCode);
}

export function territoriesContainsEpt(territories: Array<{territory: string, values: []}>): boolean {
  for (const territory of territories) {
    const departmentCode = territory.territory?.slice(-2);
    if (departmentContainsEpt(departmentCode) ) {
      return true;
    }
  }
  return false;
}

// Territory perimeter display functions
export interface TerritoryData {
  allChecked: boolean;
  departmentName: string | null;
  checkedCommunes: TreeviewItem[];
  checkedEpcis: Array<{
    text: string;
    value: string;
    checkedCommunes: number;
    totalCommunes: number;
    allCommunesChecked: boolean;
    communeItems: TreeviewItem[];
  }>;
  fullEpcis: string[];
}

function buildCommuneArticle(name: string | null | undefined): string {
  if (!name) {
    return '';
  }

  const trimmed_name = name.trim();
  if (!trimmed_name) {
    return '';
  }

  const lower_name = trimmed_name.toLowerCase();

  // 1. Commune starts with "Le" → "du <name-without-Le>"
  if (lower_name.startsWith('le ')) {
    return `du ${trimmed_name.substring(3)}`;
  }

  // 2. Commune starts with "La" → "de la <name-without-La>"
  if (lower_name.startsWith('la ')) {
    return `de ${trimmed_name.substring(3)}`;
  }

  // 3. Commune starts with "Les" → "des <name-without-Les>"
  if (lower_name.startsWith('les ')) {
    return `des ${trimmed_name.substring(4)}`;
  }

  // 4. Commune starts with a vowel → "d'<name>"
  const firstChar = trimmed_name[0].toLowerCase();
  const vowelOrH = ['a', 'e', 'i', 'o', 'u', 'é'];
  if (vowelOrH.includes(firstChar)) {
    return `d'${trimmed_name}`;
  }

  // 5. Otherwise → "de <name>"
  return `de ${trimmed_name}`;
}

function buildDepartmentArticle(name: string | null | undefined): string {
  if (!name) {
    return '';
  }
  const dep_name = name.split('(')[0].trim();
  const article = frenchDepartments[dep_name];

  return `${article}${name}`;
}

function getDisplayedPerimeter(territoryTree: TreeviewItem[]): [string, string] {
  if (!territoryTree || territoryTree.length === 0) {
    return ['', ''];
  }

  const data = collectTerritoryData(territoryTree);
  const departmentExpression = buildDepartmentArticle(data.departmentName);

  // Case 1: All department
  if (data.allChecked && data.departmentName) {
    const perimeter = `le département ${departmentExpression} ainsi que ses EPCI et communes`;
    return [perimeter, data.departmentName];
  }

  // Case 2: Only one city and no EPCI
  if (data.checkedCommunes.length === 1 && data.checkedEpcis.length === 1) {
    const communeName = data.checkedCommunes[0].text;
    const perimeter =
      `la commune ${buildCommuneArticle(communeName)}` +
      (departmentExpression ? ` du département ${departmentExpression}` : '');
    return [perimeter, communeName];
  }

  // Case 3: Partial or complete EPCI(s) with or without additional cities
  return getEpciDisplay(data.checkedEpcis, data.checkedCommunes, data.departmentName);
}

function collectTerritoryData(items: TreeviewItem[]): TerritoryData {
  const result: TerritoryData = {
    fullEpcis: [],
    allChecked: true,
    departmentName: null,
    checkedCommunes: [],
    checkedEpcis: [],
  };

  if (!items?.length) {
    return result;
  }

  // Store full EPCIs and isolated communes separately
  const fullEpciSet = new Set<string>();

  const processDepartment = (item: TreeviewItem): void => {
    if (item.text) {
      result.departmentName = item.text;
    }
  };

  const processCommune = (item: TreeviewItem, parentEpci?: string): void => {
    if (item.checked === true && item.text) {
      // Only add commune if it's not part of a full EPCI
      if (!parentEpci || !fullEpciSet.has(parentEpci)) {
        result.checkedCommunes.push(item);
      }
    }
  };

  const processEpci = (item: TreeviewItem): void => {
    if (!item.text) {
      return;
    }

    const communes =
      item.children?.filter(
        (child) =>
          child.value &&
          child.value !== '' &&
          child.infos?.kind === TerritoryKind.COMMUNE
      ) || [];
    const checkedCommunes = communes.filter((c) => c.checked === true);
    const isEpciChecked =
      item.checked === true || checkedCommunes.length > 0;
    const allCommunesChecked =
      communes.length > 0 &&
      communes.every((c) => c.checked === true);

    if (isEpciChecked) {
      // Mark this EPCI as full before processing its children
      if (allCommunesChecked && item.value) {
        fullEpciSet.add(item.value);
      }

      result.checkedEpcis.push({
        text: item.text,
        value: item.value,
        checkedCommunes: checkedCommunes.length,
        totalCommunes: communes.length,
        allCommunesChecked: allCommunesChecked,
        communeItems: checkedCommunes,
      });
    }
  };

  const getChildParentEpci = (item: TreeviewItem, currentParentEpci?: string): string | undefined => {
    return item.infos?.kind === TerritoryKind.EPCI ? item.value : currentParentEpci;
  };

  const processChildren = (item: TreeviewItem, parentEpci?: string): void => {
    if (!item.children) {
      return;
    }
    for (const child of item.children) {
      processTerritoryNode(child, getChildParentEpci(item, parentEpci));
    }
  };

  const processNodeByKind = (item: TreeviewItem, kind: TerritoryKind | undefined, parentEpci?: string): void => {
    if (kind === TerritoryKind.DEPARTMENT) {
      processDepartment(item);
    } else if (kind === TerritoryKind.COMMUNE) {
      processCommune(item, parentEpci);
    } else if (kind === TerritoryKind.EPCI) {
      processEpci(item);
    }
  };

  const processTerritoryNode = (item: TreeviewItem, parentEpci?: string): void => {
    if (!item.value || item.value === '') {
      processChildren(item, parentEpci);
      return;
    }

    if (item.checked !== true) {
      result.allChecked = false;
    }

    const {kind} = item.infos || {};
    processNodeByKind(item, kind, parentEpci);
    processChildren(item, parentEpci);
  };

  for (const item of items) {
    processTerritoryNode(item);
  }
  result.fullEpcis = Array.from(fullEpciSet);
  return result;
}

function getEpciDisplay(
  checkedEpcis: Array<{
    text: string;
    value: string;
    checkedCommunes: number;
    totalCommunes: number;
    allCommunesChecked: boolean;
    communeItems: TreeviewItem[];
  }>,
  checkedCommunes: TreeviewItem[],
  departmentName: string | null
): [string, string] {
  if (checkedEpcis.length === 0) {
    return ['', ''];
  }

  const fullEpcis = checkedEpcis.filter((e) => e.allCommunesChecked);
  const epciCount = checkedEpcis.length;
  const departmentExpression = buildDepartmentArticle(departmentName);

  // Compute the total of checked communes and the total possible in all EPCI
  const totalCheckedCommunes = checkedEpcis.reduce((sum, e) => sum + e.checkedCommunes, 0);
  const totalPossibleCommunes = checkedEpcis.reduce((sum, e) => sum + e.totalCommunes, 0);

  let communeNames: string;
  let communesPart: string;
  if (checkedCommunes.length === 1) {
    communeNames = checkedCommunes[0].text;
    const communeExpression = buildCommuneArticle(communeNames);
    communesPart = `la commune ${communeExpression} du département ${departmentExpression}`;
  } else {
    const communeExpressions = checkedCommunes.map(c => buildCommuneArticle(c.text)).join(', ');
    communesPart = `les communes ${communeExpressions} du département ${departmentExpression}`;
  }


  // Determine epciModalPart and epciPromptPart
  let epciModalPart: string = '';
  let epciPromptPart: string = '';

  if (epciCount === 1) {
    // only one EPCI
    if (fullEpcis.length === 1) {
      // full EPCI -> only display the name
      epciModalPart = fullEpcis[0].text;
      epciPromptPart = `l'EPCI ${fullEpcis[0].text} ainsi que toutes ses communes`;
    } else {
      // incomplete EPCI -> display the name with the count
      epciModalPart = `${checkedEpcis[0].text} (${totalCheckedCommunes}/${totalPossibleCommunes} communes)`;
      epciPromptPart = communesPart;
    }
  } else if (epciCount > 1) {
    // several EPCI
    if (fullEpcis.length === epciCount) {
      // all EPCI are complete -> no count
      epciModalPart = `${epciCount} EPCI`;
      epciPromptPart = `les EPCI ${fullEpcis.map(e => e.text).join(', ')} ainsi que toutes leurs communes`;
    } else if (fullEpcis.length === 1) {
      // only one complete EPCI -> display the name with the count
      epciModalPart = `${epciCount} EPCI (${totalCheckedCommunes}/${totalPossibleCommunes} communes)`;
      epciPromptPart = `l'EPCI ${fullEpcis[0].text} ainsi que ${communesPart}`;
    } else if (fullEpcis.length > 0) {
      // at least one EPCI is incomplete -> display with count
      epciModalPart = `${epciCount} EPCI (${totalCheckedCommunes}/${totalPossibleCommunes} communes)`;
      epciPromptPart = `les EPCI ${fullEpcis.map(e => e.text).join(', ')} ainsi que ${communesPart}`;
    } else {
      // no complete EPCI -> display the communes
      epciModalPart = `${epciCount} EPCI (${totalCheckedCommunes}/${totalPossibleCommunes} communes)`;
      epciPromptPart = `${communesPart}`;
    }
  }

  return [epciPromptPart, epciModalPart];
}
